import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../../config/config";
import Wrapper from "../../../components/Wrapper";
import title from "../../../components/title";
import { useSelector } from "react-redux";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Skeleton,
  LinearProgress,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as PaidIcon,
  Schedule as PendingIcon,
  Notifications as ReminderIcon,
  TrendingUp as TrendingIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Send as SendIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";

export default function RentTracker() {
  title("Rent Tracker");
  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);
  const userId = validateUser?.UserValidate?.data?.user?._id;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [reminderDialog, setReminderDialog] = useState({ open: false, tenant: null });
  const [recordPaymentDialog, setRecordPaymentDialog] = useState({ open: false, tenant: null });
  const [paymentAmount, setPaymentAmount] = useState("");
  const [busy, setBusy] = useState(false);

  // The picture comes from the rent bills themselves (server: utils/rentOverview),
  // the same ones the tenant pays — not from a flag on the tenant record.
  const fetchOverview = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/landlord/rent-overview/${userId}`);
      setRows(data?.data?.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load rent data");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Shape each row the way the table below already reads it.
  const tenantsWithRentInfo = rows.map((r) => ({
    _id: r.tenantId,
    name: r.tenantName,
    contactNumber: r.contactNumber,
    property: { flatName: r.flatName, flatNumber: r.flatNumber },
    flatId: r.flatId,
    billId: r.rent.billId,
    outstanding: r.outstanding,
    rentInfo: {
      status: r.rent.status,
      daysOverdue: r.rent.daysOverdue,
      lateFee: r.rent.lateFee,
      rentDueDate: r.rentDueDate,
      graceDays: r.gracePeriodDays,
      monthlyRent: r.rent.amount || r.monthlyRent,
      totalDue: r.rent.total,
    },
  }));

  // Filter tenants based on tab
  const filteredTenants = tenantsWithRentInfo.filter((t) => {
    switch (tabValue) {
      case 0: return true; // All
      case 1: return t.rentInfo.status === "paid";
      case 2: return ["pending", "grace", "not_billed"].includes(t.rentInfo.status);
      case 3: return t.rentInfo.status === "overdue";
      default: return true;
    }
  });

  // Calculate summary stats
  const stats = {
    totalExpected: tenantsWithRentInfo.reduce((sum, t) => sum + t.rentInfo.monthlyRent, 0),
    totalCollected: tenantsWithRentInfo
      .filter((t) => t.rentInfo.status === "paid")
      .reduce((sum, t) => sum + t.rentInfo.monthlyRent, 0),
    totalPending: tenantsWithRentInfo
      .filter((t) => t.rentInfo.status !== "paid")
      .reduce((sum, t) => sum + t.rentInfo.totalDue, 0),
    paidCount: tenantsWithRentInfo.filter((t) => t.rentInfo.status === "paid").length,
    pendingCount: tenantsWithRentInfo.filter((t) => ["pending", "grace", "not_billed"].includes(t.rentInfo.status)).length,
    overdueCount: tenantsWithRentInfo.filter((t) => t.rentInfo.status === "overdue").length,
  };

  const collectionRate = stats.totalExpected > 0
    ? Math.round((stats.totalCollected / stats.totalExpected) * 100)
    : 0;

  // Send the tenant a reminder (bell notification + email, with the owner's
  // figures) — this used to only show a toast.
  const handleSendReminder = async () => {
    const tenant = reminderDialog.tenant;
    setBusy(true);
    try {
      await axios.post(`${BASE_URL}/send-bill-reminder`, {
        tenantId: tenant._id,
        flatId: tenant.flatId,
        billType: "Rent",
        amount: tenant.rentInfo.totalDue,
        dueDate: tenant.rentInfo.rentDueDate,
        daysOverdue: tenant.rentInfo.daysOverdue,
      });
      toast.success(`Reminder sent to ${tenant.name}`, { position: "top-center" });
      setReminderDialog({ open: false, tenant: null });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send reminder");
    } finally {
      setBusy(false);
    }
  };

  // Rent received outside OTS (cash / bank): approving the rent bill is what
  // settles it, tells the tenant, and clears it from their dues.
  const handleRecordPayment = async () => {
    const tenant = recordPaymentDialog.tenant;
    if (!tenant?.billId) return;
    setBusy(true);
    try {
      await axios.post(`${BASE_URL}/approve-rent-bill/${tenant.billId}`, {
        approvalStatus: "approved",
      });
      toast.success(`${tenant.name}'s rent marked as received`, { position: "top-center" });
      setRecordPaymentDialog({ open: false, tenant: null });
      setPaymentAmount("");
      fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to mark rent as received");
    } finally {
      setBusy(false);
    }
  };

  // Status chip component
  const StatusChip = ({ status }) => {
    const config = {
      paid: { color: "success", icon: <PaidIcon />, label: "Paid" },
      pending: { color: "warning", icon: <PendingIcon />, label: "Pending" },
      grace: { color: "info", icon: <CalendarIcon />, label: "Grace Period" },
      not_billed: { color: "default", icon: <PendingIcon />, label: "Not billed yet" },
      overdue: { color: "error", icon: <WarningIcon />, label: "Overdue" },
    };
    const { color, icon, label } = config[status] || config.pending;
    return <Chip icon={icon} label={label} color={color} size="small" />;
  };

  if (loading) {
    return (
      <>
        <Wrapper />
        <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>Rent Tracker</Typography>
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        </div>
      </>
    );
  }

  return (
    <>
      <Wrapper />
      <div className={`${open ? "sidebar-open" : "sidebar-closed"}`}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">Rent Tracker</Typography>
            <Typography variant="body2" color="text.secondary">
              {moment().format("MMMM YYYY")} - Track and manage rent payments
            </Typography>
          </Box>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: "success.light" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body2" color="success.dark">Collected</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.dark">
                      PKR {stats.totalCollected.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="success.dark">
                      {stats.paidCount} tenant(s) paid
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: "success.main" }}>
                    <PaidIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: "warning.light" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body2" color="warning.dark">Pending</Typography>
                    <Typography variant="h5" fontWeight="bold" color="warning.dark">
                      PKR {stats.totalPending.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="warning.dark">
                      {stats.pendingCount + stats.overdueCount} tenant(s)
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: "warning.main" }}>
                    <PendingIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: "error.light" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body2" color="error.dark">Overdue</Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.dark">
                      {stats.overdueCount}
                    </Typography>
                    <Typography variant="caption" color="error.dark">
                      Tenant(s) past due
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: "error.main" }}>
                    <WarningIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Collection Rate</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {collectionRate}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={collectionRate}
                      sx={{ mt: 1, height: 6, borderRadius: 3 }}
                      color={collectionRate > 70 ? "success" : collectionRate > 40 ? "warning" : "error"}
                    />
                  </Box>
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    <TrendingIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={`All (${tenantsWithRentInfo.length})`} />
            <Tab label={`Paid (${stats.paidCount})`} />
            <Tab label={`Pending (${stats.pendingCount})`} />
            <Tab label={`Overdue (${stats.overdueCount})`} />
          </Tabs>
        </Paper>

        {/* Tenants Table */}
        {filteredTenants.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <PersonIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
            <Typography color="text.secondary">
              {tabValue === 0 ? "No active tenants found" : "No tenants in this category"}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell align="right">Monthly Rent</TableCell>
                  <TableCell align="right">Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total Due</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTenants.map((tenant) => (
                  <TableRow
                    key={tenant._id}
                    sx={{
                      "&:hover": { bgcolor: "grey.50" },
                      bgcolor: tenant.rentInfo.status === "overdue" ? "error.50" : "inherit",
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 36, height: 36 }}>
                          {tenant.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {tenant.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tenant.contactNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{tenant.property?.flatName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tenant.property?.flatNumber}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        PKR {tenant.rentInfo.monthlyRent.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {tenant.rentInfo.rentDueDate}th of month
                      </Typography>
                      {tenant.rentInfo.daysOverdue > 0 && (
                        <Typography variant="caption" color="error">
                          {tenant.rentInfo.daysOverdue} days overdue
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={tenant.rentInfo.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color={tenant.rentInfo.lateFee > 0 ? "error" : "inherit"}>
                        PKR {tenant.rentInfo.totalDue.toLocaleString()}
                      </Typography>
                      {tenant.rentInfo.lateFee > 0 && (
                        <Typography variant="caption" color="error" display="block">
                          +{tenant.rentInfo.lateFee} late fee
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                        <Tooltip title="Send Reminder">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setReminderDialog({ open: true, tenant })}
                            disabled={tenant.rentInfo.status === "paid"}
                          >
                            <ReminderIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Record Payment">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => {
                              setPaymentAmount(tenant.rentInfo.totalDue.toString());
                              setRecordPaymentDialog({ open: true, tenant });
                            }}
                            disabled={tenant.rentInfo.status === "paid"}
                          >
                            <ReceiptIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Expected vs Collected Summary */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Monthly Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2">Expected This Month:</Typography>
                <Typography fontWeight="bold">PKR {stats.totalExpected.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2">Collected:</Typography>
                <Typography fontWeight="bold" color="success.main">
                  PKR {stats.totalCollected.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Remaining:</Typography>
                <Typography fontWeight="bold" color="warning.main">
                  PKR {stats.totalPending.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h3" fontWeight="bold" color={collectionRate > 70 ? "success.main" : "warning.main"}>
                    {collectionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Collection Rate
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </div>

      {/* Send Reminder Dialog */}
      <Dialog open={reminderDialog.open} onClose={() => setReminderDialog({ open: false, tenant: null })}>
        <DialogTitle>
          Send Payment Reminder
          <IconButton
            onClick={() => setReminderDialog({ open: false, tenant: null })}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            A reminder will be sent to {reminderDialog.tenant?.name} for pending rent payment.
          </Alert>
          <Typography variant="body2">
            <strong>Property:</strong> {reminderDialog.tenant?.property?.flatName}
          </Typography>
          <Typography variant="body2">
            <strong>Amount Due:</strong> PKR {reminderDialog.tenant?.rentInfo?.totalDue?.toLocaleString()}
          </Typography>
          <Typography variant="body2">
            <strong>Contact:</strong> {reminderDialog.tenant?.contactNumber}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialog({ open: false, tenant: null })}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={handleSendReminder} disabled={busy}>
            Send Reminder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={recordPaymentDialog.open} onClose={() => setRecordPaymentDialog({ open: false, tenant: null })}>
        <DialogTitle>
          Record Payment
          <IconButton
            onClick={() => setRecordPaymentDialog({ open: false, tenant: null })}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Confirm you have received this month's rent from <strong>{recordPaymentDialog.tenant?.name}</strong> (cash or bank transfer). The tenant is notified and the bill is marked paid.
          </Typography>
          <TextField
            fullWidth
            label="Payment Amount"
            type="number"
            value={paymentAmount}
            disabled
            helperText="Rent + any late fee due"
            InputProps={{
              startAdornment: <InputAdornment position="start">PKR</InputAdornment>,
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordPaymentDialog({ open: false, tenant: null })}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<PaidIcon />}
            onClick={handleRecordPayment}
            disabled={busy || !recordPaymentDialog.tenant?.billId}
          >
            Mark as received
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </>
  );
}
